import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  FiGrid, FiFileText, FiBook, FiSettings, 
  FiUsers, FiActivity, FiEye, FiEdit 
} from 'react-icons/fi';
import { HiQrcode } from 'react-icons/hi';
import { api } from '../../api';

const StatCard = ({ title, value, icon: Icon, color, link }) => (
  <Link to={link} className="block">
    <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-mountain-gray-600 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-mountain-gray-900 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  </Link>
);

const AdminDashboard = () => {
  const { data: categoriesResponse } = useQuery('categories', () => api.getCategories());
  const { data: sectionsResponse } = useQuery('allSections', () => api.getSections());
  const { data: guestbookResponse } = useQuery('adminGuestbook', () => api.getAdminGuestbookEntries());
  const { data: settingsResponse } = useQuery('settings', () => api.getSettings());
  
  const categories = categoriesResponse?.data;
  const sections = sectionsResponse?.data;
  const guestbook = guestbookResponse?.data;
  const settings = settingsResponse?.data;

  const stats = [
    {
      title: 'Categories',
      value: categories?.length || 0,
      icon: FiGrid,
      color: 'bg-lodge-brown-600',
      link: '/admin/categories'
    },
    {
      title: 'Sections',
      value: sections?.length || 0,
      icon: FiFileText,
      color: 'bg-pine-green-600',
      link: '/admin/sections'
    },
    {
      title: 'Guest Book Entries',
      value: guestbook?.length || 0,
      icon: FiBook,
      color: 'bg-sky-blue-600',
      link: '/admin/guestbook'
    },
    {
      title: 'QR Codes',
      value: (categories?.length || 0) + (sections?.length || 0),
      icon: HiQrcode,
      color: 'bg-mountain-gray-600',
      link: '/admin/qr-codes'
    }
  ];

  const recentActivity = [
    { icon: FiEdit, text: 'Ready to add content', time: 'Just now' },
    { icon: FiSettings, text: 'Settings configured', time: '2 minutes ago' },
    { icon: FiUsers, text: 'Admin account created', time: '5 minutes ago' },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-display text-lodge-brown-800">Dashboard</h1>
        <p className="text-mountain-gray-600 mt-2">
          Welcome to your guidebook admin panel
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-mountain-gray-900 mb-4">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <Link
              to="/admin/sections"
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <FiFileText className="w-5 h-5 text-lodge-brown-600" />
                <span className="text-mountain-gray-700">Add New Section</span>
              </div>
              <span className="text-mountain-gray-400">→</span>
            </Link>
            <Link
              to="/admin/guestbook"
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <FiBook className="w-5 h-5 text-pine-green-600" />
                <span className="text-mountain-gray-700">Moderate Guest Book</span>
              </div>
              <span className="text-mountain-gray-400">→</span>
            </Link>
            <Link
              to="/admin/qr-codes"
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <HiQrcode className="w-5 h-5 text-sky-blue-600" />
                <span className="text-mountain-gray-700">Generate QR Codes</span>
              </div>
              <span className="text-mountain-gray-400">→</span>
            </Link>
            <Link
              to="/admin/settings"
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <FiSettings className="w-5 h-5 text-mountain-gray-600" />
                <span className="text-mountain-gray-700">Configure Settings</span>
              </div>
              <span className="text-mountain-gray-400">→</span>
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-mountain-gray-900 mb-4">
            Recent Activity
          </h2>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <activity.icon className="w-4 h-4 text-mountain-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="text-mountain-gray-700">{activity.text}</p>
                  <p className="text-sm text-mountain-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Getting Started */}
      <div className="mt-8 bg-lodge-brown-50 rounded-xl p-6">
        <h2 className="text-xl font-semibold text-lodge-brown-800 mb-2">
          Getting Started
        </h2>
        <p className="text-lodge-brown-700 mb-4">
          Here's how to set up your guidebook:
        </p>
        <ol className="list-decimal list-inside space-y-2 text-lodge-brown-700">
          <li>Review and organize your categories in the Categories section</li>
          <li>Add content to each section with text, images, and videos</li>
          <li>Configure your house settings and notification preferences</li>
          <li>Generate and print QR codes for each room/area</li>
          <li>Test the guidebook from a guest's perspective</li>
        </ol>
      </div>
    </div>
  );
};

export default AdminDashboard;